import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SafeUrl } from '@angular/platform-browser';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';

export interface ImageDialogData {
  imageUrl: string | SafeUrl;
  description: string;
  showControls?: boolean;
  initialScale?: number;
  minScale?: number;
}

@Component({
  selector: 'app-image-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    ZoomControlComponent,
    ZoomableDirective
  ],
  template: `
    <div class="image-dialog-container">
      <div
        #zoom="appZoomable"
        appZoomable
        [(zoomScale)]="zoomScale"
        [minScale]="data?.minScale ?? 0.3"
        [maxScale]="5.0"
        class="image-dialog-body"
        [class.grabbing]="zoom.isDragging()"
      >
        @if (data?.showControls !== false) {
          <app-zoom-control
            class="zoom-toolbar"
            [(zoomScale)]="zoom.zoomScale"
            [minScale]="data?.minScale ?? 0.3"
            [maxScale]="5.0"
            [showClose]="true"
            (close)="closeDialog()"
            (reset)="zoom.resetZoom()"
            (click)="$event.stopPropagation()"
          />
        } @else {
          <button
            mat-icon-button
            class="close-btn-standalone"
            (click)="closeDialog(); $event.stopPropagation()"
            [matTooltip]="'Global.Close' | translate"
          >
            <mat-icon>close</mat-icon>
          </button>
        }

        <div
          class="image-render-area"
          [style.transform]="'translate(' + zoom.panX() + 'px, ' + zoom.panY() + 'px) scale(' + zoom.zoomScale() + ')'"
          [style.transition]="zoom.isDragging() ? 'none' : 'transform 0.2s ease-out'"
          style="transform-origin: center center;"
        >
          <img [src]="data.imageUrl" [alt]="data.description || 'Zoomed image'" />
        </div>
      </div>
    </div>
  `,
  styleUrl: './image-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageDialogComponent implements OnInit {
  data: ImageDialogData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ImageDialogComponent>);

  zoomScale = signal<number>(1.0);

  ngOnInit(): void {
    if (this.data?.initialScale !== undefined) {
      this.zoomScale.set(this.data.initialScale);
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
